import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { CreditCard, Landmark, Banknote, AlertTriangle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { fmtTRY, fmtQty4  } from "../lib/format";
import { errText } from "../lib/errors";
import api from "../lib/api";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [paytrToken, setPaytrToken] = useState("");
  const [payment, setPayment] = useState("havale");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: user?.name || "", email: user?.email || "", phone: "", city: "", district: "", address: "", postal_code: "", note: "" });
  const coupon = location.state?.coupon || null;

  useEffect(() => { api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {}); }, []);
  useEffect(() => { if (params.get("iptal")) toast.error("Ödeme iptal edildi"); }, [params]);
  useEffect(() => {
    if (!settings) return;
    if (payment === "havale" && settings.havale_enabled === false) setPayment(settings.kapida_enabled !== false ? "kapida" : (settings.sanal_pos_enabled ? "sanalpos" : ""));
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (items.length) trackEvent("begin_checkout", { currency: "TRY", value: Math.round(subtotal * 100) / 100, items: items.map((i) => ({ item_id: i.product_id, item_name: i.name, price: i.price, quantity: i.quantity })) }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const freeLimit = settings?.free_shipping_limit ?? 750;
  const shippingFee = settings?.shipping_fee ?? 49.9;
  const estShipping = subtotal >= freeLimit ? 0 : shippingFee;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.full_name || !form.email || !form.phone || !form.city || !form.address) {
      toast.error("Lütfen teslimat bilgilerini eksiksiz doldurun");
      return;
    }
    setLoading(true);
    try {
      const { data: order } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant: i.variant })),
        address: { full_name: form.full_name, phone: form.phone, city: form.city, district: form.district, address: form.address, postal_code: form.postal_code },
        email: form.email,
        payment_method: payment,
        coupon_code: coupon,
        note: form.note,
      });
      sessionStorage.setItem(`order_${order.order_number}`, order.email);
      if (payment === "sanalpos") {
        const { data } = await api.post("/payments/iyzico/initialize", { order_id: order.order_id, origin_url: window.location.origin });
        clear();
        window.location.href = data.payment_page_url;
        return;
      }
      if (payment.startsWith("pp_")) {
        const ppId = payment.slice(3);
        const pp = (settings?.payment_providers || []).find((x) => x.provider_id === ppId);
        if (pp?.type === "iyzico") {
          const { data } = await api.post("/payments/iyzico/initialize", { order_id: order.order_id, origin_url: window.location.origin, provider_id: ppId });
          clear();
          window.location.href = data.payment_page_url;
          return;
        }
        if (pp?.type === "paytr") {
          const { data } = await api.post("/payments/paytr/initialize", { order_id: order.order_id, origin_url: window.location.origin, provider_id: ppId });
          clear();
          setPaytrToken(data.iframe_token);
          return;
        }
        clear();
        navigate(`/siparis/${order.order_number}`);
        return;
      }
      clear();
      navigate(`/siparis/${order.order_number}`);
    } catch (err) {
      toast.error(errText(err) || "Sipariş oluşturulamadı");
      setLoading(false);
    }
  };

  if (items.length === 0 && !paytrToken) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <p className="font-serif text-2xl text-stone-800">Sepetiniz boş</p>
        <Link to="/urunler" className="text-forest font-semibold mt-4 inline-block">Ürünlere dön</Link>
      </div>
    );
  }

  const inputCls = "w-full h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest";

  return (
    <div data-testid="checkout-page" className="max-w-6xl mx-auto px-4 py-10">
      {paytrToken && (
        <div data-testid="paytr-iframe-wrap" className="fixed inset-0 z-50 bg-white flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
            <p className="font-semibold text-stone-800">PayTR Güvenli Ödeme</p>
            <button data-testid="paytr-close" onClick={() => setPaytrToken("")} className="text-sm font-semibold text-stone-500 hover:text-stone-800">Kapat</button>
          </div>
          <iframe title="PayTR Güvenli Ödeme" src={`https://www.paytr.com/odeme/guvenli/${paytrToken}`} className="flex-1 w-full border-0" allow="payment *" />
        </div>
      )}
      <h1 className="font-serif text-3xl sm:text-4xl text-forest">Sipariş</h1>
      <div className="flex items-center gap-2 mt-3 text-xs sm:text-sm text-stone-500">
        {["Adres", "Kargo", "Ödeme", "Onay"].map((s, i) => (
          <span key={s} className="flex items-center gap-2"><span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${i < 3 ? "bg-forest text-white" : "bg-stone-200"}`}>{i + 1}</span>{s}{i < 3 && <span className="w-6 h-px bg-stone-300" />}</span>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white rounded-2xl border border-stone-200/80 p-6">
            <h2 className="font-semibold text-lg text-stone-900">Teslimat Bilgileri</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <input data-testid="checkout-name" className={inputCls} placeholder="Ad Soyad *" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
              <input data-testid="checkout-phone" className={inputCls} placeholder="Telefon *" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              <input data-testid="checkout-email" type="email" className={inputCls} placeholder="E-posta *" value={form.email} onChange={(e) => set("email", e.target.value)} />
              <input data-testid="checkout-city" className={inputCls} placeholder="İl *" value={form.city} onChange={(e) => set("city", e.target.value)} />
              <input data-testid="checkout-district" className={inputCls} placeholder="İlçe" value={form.district} onChange={(e) => set("district", e.target.value)} />
              <input data-testid="checkout-postal" className={inputCls} placeholder="Posta Kodu" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
              <textarea data-testid="checkout-address" className={`${inputCls} h-auto py-3 sm:col-span-2`} rows={2} placeholder="Adres *" value={form.address} onChange={(e) => set("address", e.target.value)} />
              <textarea data-testid="checkout-note" className={`${inputCls} h-auto py-3 sm:col-span-2`} rows={2} placeholder="Sipariş notu (opsiyonel)" value={form.note} onChange={(e) => set("note", e.target.value)} />
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-stone-200/80 p-6">
            <h2 className="font-semibold text-lg text-stone-900">Kargo</h2>
            <label className="flex items-center gap-3 mt-4 border border-stone-200 rounded-xl p-4 cursor-pointer hover:border-forest transition-colors">
              <input type="radio" checked readOnly className="accent-forest" />
              <div className="flex-1">
                <p className="font-medium text-sm">Standart Kargo (1-3 iş günü)</p>
                <p className="text-xs text-stone-500">Kırılmaya karşı özel koruyucu ambalaj</p>
              </div>
              <p className="font-semibold text-forest text-sm">{estShipping === 0 ? "Ücretsiz" : fmtTRY(estShipping)}</p>
            </label>
          </section>

          <section className="bg-white rounded-2xl border border-stone-200/80 p-6">
            <h2 className="font-semibold text-lg text-stone-900">Ödeme Yöntemi</h2>
            <div className="space-y-3 mt-4">
              {settings?.sanal_pos_enabled && (
                <label data-testid="payment-sanalpos" className={`flex items-center gap-3 border rounded-xl p-4 cursor-pointer transition-colors ${payment === "sanalpos" ? "border-forest bg-forest/5" : "border-stone-200 hover:border-forest"}`}>
                  <input type="radio" name="payment" checked={payment === "sanalpos"} onChange={() => setPayment("sanalpos")} className="accent-forest" />
                  <CreditCard className="w-5 h-5 text-gold-dark" />
                  <div><p className="font-medium text-sm">{settings.sanal_pos_label || "Sanal POS"}</p><p className="text-xs text-stone-500">Tüm kartlar, taksit imkânı (1-12), 3D Secure güvenli ödeme</p></div>
                </label>
              )}
              {(settings?.payment_providers || []).map((pp) => (
                <label key={pp.provider_id} data-testid={`payment-pp-${pp.provider_id}`} className={`flex items-center gap-3 border rounded-xl p-4 cursor-pointer transition-colors ${payment === `pp_${pp.provider_id}` ? "border-forest bg-forest/5" : "border-stone-200 hover:border-forest"}`}>
                  <input type="radio" name="payment" checked={payment === `pp_${pp.provider_id}`} onChange={() => setPayment(`pp_${pp.provider_id}`)} className="accent-forest" />
                  <CreditCard className="w-5 h-5 text-gold-dark" />
                  <div><p className="font-medium text-sm">{pp.name}{pp.mode === "sandbox" ? " (Test)" : ""}</p><p className="text-xs text-stone-500">{pp.type === "iyzico" ? "Taksit imkânı (1-12), 3D Secure güvenli ödeme" : "Online kart ödemesi — ödeme sayfasına yönlendirilirsiniz"}</p></div>
                </label>
              ))}
              {settings?.havale_enabled !== false && (
              <label data-testid="payment-havale" className={`flex items-center gap-3 border rounded-xl p-4 cursor-pointer transition-colors ${payment === "havale" ? "border-forest bg-forest/5" : "border-stone-200 hover:border-forest"}`}>
                <input type="radio" name="payment" checked={payment === "havale"} onChange={() => setPayment("havale")} className="accent-forest" />
                <Landmark className="w-5 h-5 text-forest" />
                <div><p className="font-medium text-sm">Havale / EFT</p><p className="text-xs text-stone-500">Sipariş sonrası banka bilgileri gösterilir</p></div>
              </label>
              )}
              {settings?.kapida_enabled !== false && (
              <label data-testid="payment-kapida" className={`flex items-center gap-3 border rounded-xl p-4 cursor-pointer transition-colors ${payment === "kapida" ? "border-forest bg-forest/5" : "border-stone-200 hover:border-forest"}`}>
                <input type="radio" name="payment" checked={payment === "kapida"} onChange={() => setPayment("kapida")} className="accent-forest" />
                <Banknote className="w-5 h-5 text-forest" />
                <div><p className="font-medium text-sm">Kapıda Ödeme</p><p className="text-xs text-stone-500">Teslimatta nakit veya kart</p></div>
              </label>
              )}
            </div>
            {payment === "havale" && settings?.bank_iban && (
              <div data-testid="bank-info" className="mt-4 bg-cream-dark/60 rounded-xl p-4 text-sm">
                <p className="font-semibold text-stone-900">{settings.bank_name}</p>
                <p className="text-stone-600 mt-1">{settings.bank_holder}</p>
                <p className="font-mono text-forest font-semibold mt-1">{settings.bank_iban}</p>
                <p className="text-xs text-stone-500 mt-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Açıklamaya sipariş numaranızı yazmayı unutmayın.</p>
              </div>
            )}
          </section>
        </div>

        <aside className="bg-white rounded-2xl border border-stone-200/80 p-6 h-fit sticky top-40">
          <h2 className="font-semibold text-lg text-stone-900">Sipariş Özeti</h2>
          <div className="mt-4 space-y-3 max-h-56 overflow-y-auto">
            {items.map((i) => (
              <div key={`${i.product_id}-${i.variant}`} className="flex gap-3 items-center">
                <img src={i.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-cream-dark" />
                <div className="flex-1 min-w-0"><p className="text-sm font-medium line-clamp-1">{i.name}</p><p className="text-xs text-stone-500">x{fmtQty4(i.quantity, i.unit || "")}</p></div>
                <p className="text-sm font-semibold">{fmtTRY(i.price * i.quantity)}</p>
              </div>
            ))}
          </div>
          <dl className="mt-5 space-y-2 text-sm border-t border-stone-100 pt-4">
            <div className="flex justify-between"><dt className="text-stone-500">Ara Toplam</dt><dd>{fmtTRY(subtotal)}</dd></div>
            <div className="flex justify-between"><dt className="text-stone-500">Kargo</dt><dd>{estShipping === 0 ? "Ücretsiz" : fmtTRY(estShipping)}</dd></div>
            {coupon && <div className="flex justify-between text-green-700"><dt>Kupon</dt><dd>{coupon}</dd></div>}
          </dl>
          <p className="text-xs text-stone-400 mt-3">Kupon indirimi ve kesin toplam sipariş onayında hesaplanır.</p>
          <button data-testid="place-order-button" onClick={submit} disabled={loading} className="w-full mt-5 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3.5 rounded-xl transition-colors">
            {loading ? "İşleniyor..." : payment === "sanalpos" ? "Sanal POS ile Öde" : "Siparişi Onayla"}
          </button>
          <p className="text-[11px] text-stone-400 mt-3 text-center">Sipariş vererek <Link to="/sayfa/mesafeli-satis-sozlesmesi" className="underline">Mesafeli Satış Sözleşmesi</Link>'ni kabul etmiş olursunuz.</p>
        </aside>
      </div>
    </div>
  );
}
